import React, { memo, ReactNode, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import { Box, Divider, Grid, Theme, Typography, useMediaQuery, Zoom } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { getDisabledEventStatus, MARKETS_SLUGS_MAP } from 'utils/transform';
import { formatDateTime } from 'utils/format';
import { Event } from 'api/general/models';
import { DetectMarketSelectors } from 'store/selectors';
import Image from 'components/common/Image';
import Flag from 'components/common/Flag';
import TextTransformer from 'components/common/TextTransformer';
import { routes } from 'utils/routing';
import sortParentsAndChildren from 'utils/sortParentsAndChildren';
import Label from 'components/common/Label';
import { isSameDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import OrganizationLabelList from '../organization/OrganizationLabelList';
import EventSummary from '../event/EventSummary';
import IconPin from '../svg/icon-pin.svg';

interface EventInfoV2Props {
  secondary?: boolean;
  event: Event;
  imagePopUp?: ReactNode;
  isPopUpToggled?: boolean;
  categoryTag?: boolean;
  dateTimeTag?: boolean;
  multipleTimeTag?: boolean;
  disabled?: boolean;
  isMainPage?: boolean;
  children?: ReactNode;
  share?: ReactNode;
  like?: ReactNode;
  calendar?: ReactNode;
  onMainImageClick?: (image: any) => void;
  frame?: boolean;
  hasUserInternalPassport?: boolean;
}

interface StyleProps {
  dontBrake: boolean;
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  container: {
    minHeight: 216,
    paddingLeft: 80,
    paddingRight: 80,
    [theme.breakpoints.down('md')]: {
      paddingLeft: 0,
      paddingRight: 0,
    },
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 16,
      paddingRight: 16,
    },
    ...theme.mixins.scrollBarOverflowYAuto,
    overflowY: 'auto',
    color: theme.palette.custom.white,
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 
    100%), ${theme.palette.background.default};`,
    backgroundBlendMode: 'soft-light, normal',
    [theme.breakpoints.up('md')]: {
      height: '100%',
      marginTop: -12,
    },
    '&.secondary': {
      color: theme.palette.text.primary,
      [theme.breakpoints.down('md')]: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'stretch',
      },
    },
  },
  cursor: {
    cursor: 'pointer',
  },
  imageContainer: {
    top: 24,
    position: 'absolute',
    minWidth: 384,
    minHeight: 239,
    background: theme.palette.background.default,
    [theme.breakpoints.down('md')]: {
      top: 64,
      left: '50%',
      transform: 'translateX(-50%)',
      minWidth: 640,
      height: 398,
    },
    [theme.breakpoints.down('sm')]: {
      top: 64,
      // left: '50%',
      // transform: 'translateX(-50%)',
      minWidth: 390,
      height: 243,
    },
    zIndex: 100,
    '.secondary &': {
      [theme.breakpoints.down('md')]: {
        width: 104,
        overflow: 'hidden',
        margin: theme.spacing(3, 0),
        '& img': {
          borderTopRightRadius: 6,
          borderBottomRightRadius: 6,
          [theme.breakpoints.down('sm')]: {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          },
        },
      },
    },
  },
  contentContainer: {
    flexShrink: 0,
    padding: theme.spacing(4, 4, 0),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(4, 0, 0),
    },
    '.secondary &': {
      padding: theme.spacing(3, 4, 4),
      [theme.breakpoints.down('md')]: {
        padding: theme.spacing(3, 2),
        width: 'calc(100% - 104px)',
        justifyContent: 'space-between',
      },
    },
  },
  title: {
    marginLeft: 8,
    ...(!props?.dontBrake && {
      wordBreak: 'break-word',
    }),
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(0),
    },
    [theme.breakpoints.down('md')]: {
      ...theme.typography.h5,
    },
    '.secondary &': {
      ...theme.typography.h5,
      color: theme.palette.text.primary,
      [theme.breakpoints.down('md')]: {
        ...theme.typography.subtitle2,
        marginBottom: theme.spacing(1),
      },
    },
  },
  hosts: {
    paddingTop: 8,
    marginBottom: 40,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 0,
    },
    marginLeft: 8,
  },
  divider: {
    padding: theme.spacing(3, 0),
  },
  labelContainer: {
    '&:not(:empty)': {
      margin: theme.spacing(-0.25),
      lineHeight: 0,
      '& > *': {
        margin: theme.spacing(0.25),
        '& > .MuiGrid-item': {
          padding: 4,
        },
      },
    },
  },
  shadow: {
    position: 'relative',
    borderRadius: 8,
    '&:after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      borderRadius: 8,
      background: alpha('#000', 0.6),
    },
  },
  imagePopUpContainer: {
    position: 'absolute',
    top: 0,
    zIndex: 1,
    width: '100%',
    height: '100%',
  },
  fabContainer: {
    display: 'flex',
    position: 'absolute',
    alignItems: 'center',
    height: 0,
    bottom: 0,
    right: theme.spacing(10),
    [theme.breakpoints.down('md')]: {
      right: theme.spacing(20),
      top: 464,
    },
    [theme.breakpoints.down('sm')]: {
      right: theme.spacing(4),
      top: 308,
    },
    zIndex: 110,
  },
  contentWrapper: {
    paddingLeft: 390,
    minHeight: 288,
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(50, 0, 0),
      maxWidth: 640,
      marginRight: 'auto',
      marginLeft: 'auto',
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(30, 0, 0),
      marginRight: 'auto',
      marginLeft: 'auto',
    },
  },
  typerWrapper: {
    marginLeft: 8,
    [theme.breakpoints.down('md')]: {
      margin: '0 !important',
      maxWidth: 640,
    },
  },
  titleOrgsWrapper: {
    paddingTop: 24,
    [theme.breakpoints.down('md')]: {
      marginLeft: -8,
      maxWidth: 640,
    },
  },
}));

const BUILD_ENV = process.env.NEXT_PUBLIC_BUILD_ENV;
const isTestEnv = () => BUILD_ENV === 'test';
const isStageEnv = () => BUILD_ENV === 'stage';

const DONT_BRAKE_TITLE_EVENTS = isTestEnv() || isStageEnv() ? [837] : [6246];

const EventInfoV2: React.FC<React.PropsWithChildren<EventInfoV2Props>> = ({
  categoryTag,
  dateTimeTag,
  multipleTimeTag,
  children,
  like,
  share,
  calendar,
  onMainImageClick,
  isPopUpToggled,
  imagePopUp,
  secondary = false,
  disabled = false,
  isMainPage = false,
  event,
  frame,
  hasUserInternalPassport,
}) => {
  const {
    id,
    title,
    start_at,
    end_at,
    timezone,
    categories,
    types,
    sessions,
    images = [],
    organization,
    organization_mentions = [],
    status_display,
    tickets_sale_status,
    address,
  } = event;
  const { classes, cx } = useStyles({ dontBrake: DONT_BRAKE_TITLE_EVENTS.includes(id) });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [t] = useTranslation('public');
  const router = useRouter();
  const disabledStatus = getDisabledEventStatus(tickets_sale_status, status_display);
  const sliceOfCategories = categoryTag && categories.length !== 0 && categories;
  const dateTimeTagLabel = dateTimeTag && start_at && formatDateTime(start_at, timezone);
  const multipleTimeTagLabel =
    multipleTimeTag && Array.isArray(sessions) && sessions.length > 1 && t('common:general.multipleTimes');
  const mainImage = images.find(({ main }: any) => main);
  const hosts = [{ organization }, ...organization_mentions];
  const market = useSelector(DetectMarketSelectors.getSelectedMarket);
  const variant = isMobile ? 'square' : 'rectangle';
  const isSmallMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [hostedByMargin, setHostedByMargin] = useState(8);
  const less790 = useMediaQuery('(max-width: 790px)');
  const less930 = useMediaQuery('(max-width: 930px)');

  let handleImageClick;
  if (onMainImageClick) {
    handleImageClick = () => {
      onMainImageClick(mainImage);
    };
  }

  const addressBlock = useMemo(() => {
    if (address?.city || address?.state)
      return (
        <>
          <Grid container style={{ minHeight: 34, paddingTop: 8, paddingLeft: 12 }} spacing={1}>
            <Grid item>
              <IconPin />
            </Grid>
            <Grid item xs>
              {address?.address_name ? (
                <Typography variant="body2" color="primary" style={{ minHeight: 20, fontWeight: 600 }}>
                  {address?.address_name}
                </Typography>
              ) : (
                <Typography variant="body2" color="primary">
                  {address?.address1} {address?.address1 && <br />}
                  {address?.address2}
                  {address?.address2 && <br />}
                  {address?.city && `${address?.city}, `} {address?.state}
                  {address?.postal_code && `, ${address?.postal_code}`}
                </Typography>
              )}
            </Grid>
          </Grid>
          {address?.address_name && (
            <Typography variant="body2" color="primary" style={{ paddingLeft: 32 }}>
              {address?.address1} {address?.address1 && <br />}
              {address?.address2}
              {address?.address2 && <br />}
              {address?.city && `${address?.city}, `} {address?.state}
              {address?.postal_code && `, ${address?.postal_code}`}
            </Typography>
          )}
        </>
      );
    return <div />;
  }, [
    address?.address1,
    address?.address2,
    address?.address_name,
    address?.city,
    address?.postal_code,
    address?.state,
  ]);

  const durationSameDay = start_at && isSameDay(utcToZonedTime(start_at, timezone), utcToZonedTime(end_at, timezone));
  const showDuration = useMemo(() => {
    if (event.settings.internal_passport_required && hasUserInternalPassport && less930) return true;
    if (durationSameDay && less790) return true;
    if (!durationSameDay && less930) return true;
    return false;
  }, [durationSameDay, event.settings.internal_passport_required, hasUserInternalPassport, less790, less930]);

  return (
    <Grid
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      className={cx(classes.container, { secondary })}
      container
      // direction="column"
      wrap="nowrap"
    >
      {mainImage && (
        <Grid
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          className={classes.imageContainer}
          item
        >
          <Zoom in={isPopUpToggled}>
            <div
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              className={classes.imagePopUpContainer}
            >
              {imagePopUp}
            </div>
          </Zoom>
          <Box
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            className={cx({ [classes.cursor]: onMainImageClick, [classes.shadow]: disabledStatus })}
            onClick={handleImageClick}
          >
            <Image
              src={mainImage.image.original_url}
              alt={mainImage.image.filename}
              rounded={!isSmallMobile}
              variant={isMainPage ? 'rectangle' : variant}
              priority
            />
            {disabledStatus && (
              <Flag
                text={t(`event.eventStatusDisplay`, { context: disabledStatus.title })}
                color={disabledStatus.color}
              />
            )}
          </Box>
        </Grid>
      )}
      <Grid
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        className={classes.contentWrapper}
        item
        container
      >
        <Grid
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          className={classes.contentContainer}
          item
          container
          direction="column"
          wrap="nowrap"
        >
          <Grid
            item
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            className={classes.labelContainer}
          >
            <Grid
              container
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              className={classes.typerWrapper}
            >
              {types &&
                sortParentsAndChildren(types).map((type) => (
                  <Grid item key={type.id}>
                    <Label
                      key={type.id}
                      color="extraLightPrimary"
                      textColor="primary"
                      size="small"
                      label={type.title}
                      hasHover
                      onClick={() =>
                        router.push(
                          routes.events({
                            type: type.id,
                            market_slug: MARKETS_SLUGS_MAP[market as keyof typeof MARKETS_SLUGS_MAP],
                          }).href,
                        )
                      }
                    />
                  </Grid>
                ))}
              {sliceOfCategories &&
                sliceOfCategories.map((category) => (
                  <Grid item>
                    <Label
                      key={category.id}
                      color="extraLightPrimary"
                      textColor="primary"
                      size="small"
                      label={category.title}
                      hasHover
                      onClick={() =>
                        router.push(
                          routes.events({
                            category: category.id,
                            market_slug: MARKETS_SLUGS_MAP[market as keyof typeof MARKETS_SLUGS_MAP],
                          }).href,
                        )
                      }
                    />
                  </Grid>
                ))}
              {dateTimeTagLabel && (
                <Grid item>
                  <Label color="primary" textColor="extraLightPrimary" size="small" label={dateTimeTagLabel} />
                </Grid>
              )}
              {multipleTimeTagLabel && (
                <Grid item>
                  <Label color="primary" textColor="extraLightPrimary" size="small" label={multipleTimeTagLabel} />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid
            item
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            className={classes.titleOrgsWrapper}
          >
            <TextTransformer
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              className={classes.title}
              limit={100}
              defaultFontSize="h5"
              minFontSize="h5"
              transformAfter={50}
              component="h1"
            >
              {title}
            </TextTransformer>
            {isSmallMobile && (
              <div
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                className={classes.divider}
                style={{ paddingBottom: 14 }}
              >
                <Divider />
              </div>
            )}
            <Typography
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              className={classes.hosts}
              variant="body2"
              color="textPrimary"
            >
              <Grid container spacing={1.25}>
                <Grid item style={{ alignSelf: 'start' }}>
                  <Typography
                    variant="body2"
                    style={{ marginRight: hostedByMargin, fontWeight: 500, marginTop: hostedByMargin }}
                  >
                    {t('event.eventInfo.hostedBy')}
                  </Typography>
                </Grid>
                <Grid item xs>
                  <div
                    id="headerHosts"
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      alignItems: 'center',
                      // maxWidth: 600,
                    }}
                  >
                    <OrganizationLabelList
                      frame={frame}
                      organizations={hosts}
                      disabled={disabled}
                      onChangeToText={(value: boolean) => setHostedByMargin(value ? 0 : 8)}
                    />
                  </div>
                </Grid>
              </Grid>
            </Typography>
          </Grid>
          {showDuration && (
            <>
              <Grid
                item
                xs={12}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                className={classes.divider}
              >
                <Divider />
              </Grid>
              <Grid style={{ marginLeft: -8, paddingBottom: 24 }} item xs={12}>
                <EventSummary event={event} color="primary" />
                {addressBlock}
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
      {(share || like || calendar) && (
        <div
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          className={classes.fabContainer}
        >
          <Grid container spacing={2}>
            {like && <Grid item>{like}</Grid>}
            {share && <Grid item>{share}</Grid>}
            {calendar && <Grid item>{calendar}</Grid>}
          </Grid>
        </div>
      )}
      {children && (
        <Grid
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          className={classes.childrenContainer}
          item
        >
          {(!secondary || isMobile) && (
            <Divider
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              className={classes.divider}
            />
          )}
          {children}
        </Grid>
      )}
    </Grid>
  );
};

export default memo(EventInfoV2);
